// Payload shapes for content sections (the non-product homepage/custom-page sections). Each content
// section carries an opaque JSON string on IStorefrontSection.contentConfig, shaped by the section's
// `type`. Parsers here are strictly defensive: malformed/absent JSON yields null, so a broken config
// renders nothing rather than throwing.

import { IStorefrontSection } from '../../Api/Models/Products';
import { ISearchElementStyle } from './searchPageConfig';

// --- video ---
// A single video. YouTube URLs (watch/embed/short forms) render as an embed iframe; direct
// .mp4/.webm URLs render via <video controls>. The section title is the only heading — the video
// itself has no per-video title. See VideoSection.
export interface IVideoContent {
    url?: string;
}

// --- richText ---
// Rich text is now authored as 1-3 typed column cells (the same ISectionCell shape as product
// section header/footer cells) carried on the section's `headerCells`. `markdown` is the legacy
// single-column payload, still rendered when no headerCells are present (backward compat). See
// RichTextSection.
//
// A rich text section may also carry ONE optional call-to-action button. The button lives in the
// section's contentConfig (not in a cell) so it can sit before or after the whole cell row and be
// aligned/styled independently. See RichTextSection.
export interface IRichTextButtonConfig {
    label?: string;                       // button renders ONLY when label is non-empty
    url?: string;                         // internal path -> SPA nav; absolute http(s) -> new tab noopener
    position?: 'start' | 'end';           // default 'end'
    align?: 'left' | 'center' | 'right';  // default 'left'
    style?: ISearchElementStyle;          // may carry $styleRef -> named button preset (base/hover/label parts)
}

export interface IRichTextContent {
    markdown?: string;
    button?: IRichTextButtonConfig;
}

// --- banner ---
// A rotating carousel of banner slides (crossfades when more than one active slide; a single active
// slide simply doesn't rotate). Reuses the per-banner overlay config (bannerOverlayConfig.ts) for the
// title/subtitle/CTA. See BannerSection.
export interface IContentBannerSlide {
    imageUrl?: string;
    linkUrl?: string;
    altText?: string;
    backgroundColor?: string;
    overlayConfig?: string;   // opaque IBannerOverlayConfig JSON, parsed like the hero banner
    disabled?: boolean;       // when true the slide is skipped (not shown, no dot)
}
export interface IBannerContent {
    banners?: IContentBannerSlide[];
    height?: number;          // px; default 320
    fullBleed?: boolean;      // break out to the full viewport width (marginLeft: calc(50% - 50vw))
    // 'fill' crops (object-fit: cover, the default), 'fit' letterboxes (object-fit: contain).
    imageFit?: 'fit' | 'fill';
}

// --- singleProduct ---
// Features ONE item (product, bundle, or saved-search/dynamic product) as a hero card that clicks
// through to its details page. The item arrives ALREADY RESOLVED by the BFF as section.products[0]
// (standard IProduct card shape); this config carries only the merchant's presentation choices. The
// shopper NEVER fetches the item — if section.products is empty the section renders nothing. See
// SingleProductSection.
export interface ISingleProductContent {
    itemType?: 'product' | 'bundle' | 'savedSearch';
    itemId?: string;
    blurb?: string;     // optional merchant marketing text
    ctaLabel?: string;  // CTA button text, default "View Details"
    snapshot?: object;  // editor-only display cache — IGNORE on the shopper
}

// --- divider ---
// A horizontal rule / spacer. Renders a centered line at widthPercent of the column with the given
// thickness/style/color, plus `spacing`px of vertical padding above and below. lineStyle 'none' draws
// no line — the section becomes a pure vertical spacer. Defaults (any field absent): solid, 2px, 80%,
// 24px, a theme-neutral color. See ContentSection.
export interface IDividerContent {
    lineStyle?: 'solid' | 'dashed' | 'none';
    thickness?: number;      // px line thickness
    color?: string;          // line color
    widthPercent?: number;   // 1-100, width of the line as a % of the column
    spacing?: number;        // px vertical padding above and below the line
}

// The content-section kinds. A section whose `type` is one of these is a content section; anything
// else (absent, 'product', legacy) is a product section.
export const CONTENT_SECTION_TYPES = ['video', 'richText', 'banner', 'singleProduct', 'divider'] as const;
export type ContentSectionType = typeof CONTENT_SECTION_TYPES[number];

// True when a section should render through ContentSection rather than the product-section renderer.
export function isContentSection(section: Pick<IStorefrontSection, 'type'>): boolean {
    return !!section.type && (CONTENT_SECTION_TYPES as readonly string[]).includes(section.type);
}

// Generic defensive parser for a content section's opaque JSON payload. Returns null on absence or
// malformed JSON so callers can render nothing without a guard at every call site.
export function parseContentConfig<T>(raw?: string): T | null {
    if (!raw) return null;
    try {
        return JSON.parse(raw) as T;
    } catch {
        return null;
    }
}
